import { NotificationMessageDto, NotificationMessageType } from '@/types/webapi';
import { useI18n } from 'vue-i18n';

export function useNotificationMessage() {
    const { t } = useI18n();

    function getNotificationText(notification?: NotificationMessageDto, hideBody: boolean = false) {
        if (!notification) return '';

        if (notification.type === NotificationMessageType.BookingConfirmation) {
            return t('booking.resources.bookingSuccessTitle', 'Call booked');
        }

        if (notification.type === NotificationMessageType.OnlineBookingArrangement) {
            const title = t('booking.resources.bookingDefaultTitle', 'Booking arrangement');
            return hideBody ? `<strong>${title}</strong>` : `<strong>${title}:</strong> ${notification.text}`;
        }

        if (notification.details) {
            return hideBody
                ? `<strong>${notification.text?.trim()}</strong>`
                : `<strong>${notification.text?.trim()}:</strong> ${notification.details.trim()}`;
        }

        return notification.text?.trim() ?? '';
    }

    return {
        getNotificationText
    };
}

export function getNotificationTextColor(type?: NotificationMessageType) {
    switch (type) {
        case NotificationMessageType.Warning:
        case NotificationMessageType.Deviation:
            return '#F6BC00';

        case NotificationMessageType.Cancellation:
            return 'red';

        default:
            return 'green';
    }
}

export function getDialogNotificationAlertText(type?: NotificationMessageType) {
    const { t } = useI18n();

    switch (type) {
        case NotificationMessageType.Deviation:
            return t('common.resources.deviation', 'Deviation');

        case NotificationMessageType.Warning:
            return t('common.resources.warning', 'Information');

        case NotificationMessageType.Cancellation:
            return t('common.resources.cancellation', 'Cancellation');

        case NotificationMessageType.BookingArrangement:
        case NotificationMessageType.OnlineBookingArrangement:
        case NotificationMessageType.BookingConfirmation:
            return t('common.resources.booking', 'Booking arrangement');

        default:
            return t('common.resources.notice', 'Notice');
    }
}

export function getNotificationMessageBySeverity(notificationMessages: NotificationMessageDto[]) {
    if (notificationMessages.some(e => e.type === NotificationMessageType.Cancellation)) {
        return notificationMessages.find(e => e.type === NotificationMessageType.Cancellation);
    }

    if (notificationMessages.some(e => e.type === NotificationMessageType.Deviation)) {
        return notificationMessages.find(e => e.type === NotificationMessageType.Deviation);
    }

    if (notificationMessages.some(e => e.type === NotificationMessageType.Warning)) {
        return notificationMessages.find(e => e.type === NotificationMessageType.Warning);
    }

    if (notificationMessages.some(e => e.type === NotificationMessageType.BookingConfirmation)) {
        return notificationMessages.find(e => e.type === NotificationMessageType.BookingConfirmation);
    }

    if (notificationMessages.some(e => e.type === NotificationMessageType.BookingArrangement)) {
        return notificationMessages.find(e => e.type === NotificationMessageType.BookingArrangement);
    }

    return notificationMessages.find(e => typeof e !== 'undefined');
}
